<template>
  <div class="wa-conversations" :dir="isHebrew ? 'rtl' : 'ltr'">
    <NavBar/>

    <b-container class="wa-conversations-body">
      <div class="wa-page-header">
        <h4 class="mb-0">{{ t('waConversationsTitle') }}</h4>
        <!-- Nothing on this page polls. A message that arrives while it is
             open is invisible until something refetches, and reloading the
             whole page loses the conversation you were reading. -->
        <b-button
          variant="outline-primary"
          size="sm"
          class="wa-refresh-button"
          :disabled="refreshing"
          @click="refresh"
        >
          <span class="material-icons wa-refresh-icon" :class="{ 'is-spinning': refreshing }">refresh</span>
          {{ refreshing ? t('waConversationsRefreshing') : t('waConversationsRefresh') }}
        </b-button>
      </div>
      <p class="text-muted">{{ t('waConversationsSubtitle') }}</p>

      <b-row>
        <!-- Recent contacts. Without this the page is unusable unless you
             already know the phone number you are looking for. -->
        <b-col cols="12" md="4" class="mb-4">
          <b-form-input
            v-model="phoneInput"
            class="mb-2"
            :placeholder="t('waConversationsSearchPlaceholder')"
            dir="ltr"
            @keyup.enter="loadConversation( phoneInput )"
          />
          <b-button variant="primary" size="sm" block @click="loadConversation( phoneInput )">
            {{ t('waConversationsSearchButton') }}
          </b-button>

          <div v-if="loadingRecent" class="text-muted mt-3">{{ t('waConversationsLoading') }}</div>
          <p v-else-if="!recent.length" class="text-muted mt-3">
            {{ t('waConversationsNoContacts') }}
          </p>
          <ul v-else class="wa-contact-list mt-3">
            <li
              v-for="contact in recent"
              :key="contact.phone"
              class="wa-contact"
              :class="{ 'is-active': contact.phone === activePhone }"
              @click="loadConversation( contact.phone )"
            >
              <span class="wa-contact-name">
                {{ contact.profile_name || t('waConversationsNoName') }}
              </span>
              <span class="wa-contact-phone" dir="ltr">+{{ contact.phone }}</span>
              <span v-if="contact.opted_out" class="wa-badge">{{ t('waConversationsOptedOut') }}</span>
              <span v-if="contact.last_inbound_at" class="wa-contact-date">
                {{ formatDate( contact.last_inbound_at ) }}
              </span>
            </li>
          </ul>
        </b-col>

        <b-col cols="12" md="8">
          <div v-if="loadingThread" class="text-muted">{{ t('waConversationsLoading') }}</div>
          <p v-else-if="error" class="text-danger">{{ error }}</p>
          <p v-else-if="!activePhone" class="text-muted">
            {{ t('waConversationsPickOne') }}
          </p>
          <div v-else>
            <div class="wa-thread-header">
              <strong>{{ (contact && contact.profile_name) || t('waConversationsNoName') }}</strong>
              <span dir="ltr">+{{ activePhone }}</span>
              <span v-if="contact && contact.opted_out" class="wa-badge">
                {{ t('waConversationsOptedOut') }}
              </span>
            </div>

            <!-- Demo prospects only: sends the itinerary the "Continue demo"
                 tap would have, for the ones who never tapped. -->
            <div v-if="demoRideId" class="wa-demo-send">
              <b-button
                variant="success"
                size="sm"
                :disabled="sendingDemo"
                @click="sendDemoItinerary"
              >
                {{ sendingDemo ? t('waDemoSendSending') : t('waDemoSendButton') }}
              </b-button>
              <span class="wa-tag">#{{ demoRideId }}</span>
              <span
                v-if="demoSendMessage"
                :class="demoSendOk ? 'text-success' : 'text-danger'"
              >{{ demoSendMessage }}</span>
            </div>

            <p v-if="!messages.length" class="text-muted mt-3">
              {{ t('waConversationsEmptyThread') }}
            </p>
            <div v-else class="wa-thread">
              <div
                v-for="(message, index) in orderedMessages"
                :key="index"
                class="wa-message"
                :class="['wa-' + message.direction, {
                  'is-failed': message.status === 'failed',
                  'is-rate-limited': message.status === 'rate_limited'
                }]"
              >
                <div class="wa-message-body">{{ message.body }}</div>
                <div class="wa-message-meta">
                  <span>{{ formatDateTime( message.at ) }}</span>
                  <span v-if="message.outcome" class="wa-tag">{{ message.outcome }}</span>
                  <span v-if="message.status" class="wa-tag">{{ message.status }}</span>
                  <span v-if="message.template_name" class="wa-tag">{{ message.template_name }}</span>
                  <span v-if="message.ride_id" class="wa-tag">#{{ message.ride_id }}</span>
                </div>
                <div v-if="message.error" class="wa-message-error">{{ message.error }}</div>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-container>

    <PageFooterNew/>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { translationMixin } from '@/mixins/translationMixin';
import NavBar from '@/components/NavBar.vue';
import PageFooterNew from '@/components/PageFooterNew.vue';
import whatsappConversationsApi from '@/api/whatsappConversations';
import { demoSendResult } from '@/utils/whatsappDemoSend';

export default {
  name: 'AdminWhatsappConversations',
  mixins: [translationMixin],
  components: { NavBar, PageFooterNew },
  data() {
    return {
      recent: [],
      messages: [],
      contact: null,
      activePhone: '',
      phoneInput: '',
      loadingRecent: true,
      loadingThread: false,
      refreshing: false,
      error: '',
      demoRideId: null,
      sendingDemo: false,
      demoSendMessage: '',
      demoSendOk: false,
    };
  },
  computed: {
    ...mapGetters(['apptoken']),

    // Newest first (owner's request, 2026-10-06). The server returns the
    // thread OLDEST first -- it takes the most recent MAX_MESSAGES per
    // direction, then sorts ascending to merge the two tables -- which put
    // the message you actually came to read at the bottom of a long page.
    //
    // Sorted here rather than reversed: `at` is an ISO-8601 string, so a
    // lexicographic compare IS a chronological one, and this stays correct
    // even if the server's own order ever changes. The index tiebreak keeps
    // two messages sent in the same second in a stable, repeatable order
    // instead of letting sort() decide differently between renders.
    orderedMessages() {
      return this.messages
        .map(( message, index ) => ({ message, index }))
        .sort(( a, b ) => {
          const byTime = String(b.message.at || '')
            .localeCompare(String(a.message.at || ''));
          return byTime !== 0 ? byTime : b.index - a.index;
        })
        .map(( entry ) => entry.message);
    },
  },
  async mounted() {
    await this.loadRecent();
  },
  methods: {
    // Refetches BOTH panels: the contact list on the left (a new number may
    // have written in) and the open thread on the right. Reloading only the
    // thread would leave a conversation that just started invisible, which
    // is one of the things this page exists to notice.
    //
    // activePhone, not phoneInput: the input is cleared after a successful
    // load, and the server-normalised number is the one the thread is keyed
    // on.
    async refresh() {
      if ( this.refreshing ) return;
      this.refreshing = true;
      try {
        await this.loadRecent();
        if ( this.activePhone ) {
          await this.loadConversation( this.activePhone );
        }
      } finally {
        this.refreshing = false;
      }
    },

    async loadRecent() {
      this.loadingRecent = true;
      try {
        const response = await whatsappConversationsApi.getRecent( this.apptoken );
        this.recent = response.data.conversations || [];
      } catch ( error ) {
        this.recent = [];
      } finally {
        this.loadingRecent = false;
      }
    },
    async loadConversation( phone ) {
      const trimmed = ( phone || '' ).trim();
      if ( !trimmed ) {
        return;
      }
      this.loadingThread = true;
      this.error = '';
      try {
        const response = await whatsappConversationsApi.getConversation( this.apptoken, trimmed );
        // activePhone comes from the RESPONSE, not the input: the server
        // normalises the number, so echoing what was typed would leave a
        // '+972-52-...' heading above a conversation keyed on '97252...'.
        if ( response.data.phone !== this.activePhone ) {
          this.demoSendMessage = '';
        }
        this.activePhone = response.data.phone;
        this.messages = response.data.messages || [];
        this.contact = response.data.contact;
        this.demoRideId = response.data.demo_ride_id || null;
        this.phoneInput = '';
      } catch ( error ) {
        this.error = this.t( 'waConversationsError' );
        this.messages = [];
        this.contact = null;
        this.demoRideId = null;
      } finally {
        this.loadingThread = false;
      }
    },
    // Real WhatsApp message to a prospect, so it asks first. The server
    // reuses the tap's idempotency key, so a prospect who already has the
    // itinerary gets 'already_sent', never a second copy.
    async sendDemoItinerary() {
      if ( this.sendingDemo || !this.activePhone ) return;
      const confirmed = await this.$bvModal.msgBoxConfirm(
        this.t( 'waDemoSendConfirm' ),
        { okTitle: this.t( 'waDemoSendButton' ), cancelTitle: this.t( 'cancel' ), centered: true }
      );
      if ( !confirmed ) return;

      const phone = this.activePhone;
      this.sendingDemo = true;
      this.demoSendMessage = '';
      let result;
      let outcome;
      try {
        const response = await whatsappConversationsApi.sendDemoItinerary( this.apptoken, phone );
        outcome = response.data.outcome;
        result = demoSendResult( outcome );
      } catch ( error ) {
        const code = error.response && error.response.data && error.response.data.error;
        outcome = code;
        result = demoSendResult( undefined, code );
      } finally {
        this.sendingDemo = false;
      }
      // The admin may have opened another conversation while this was in
      // flight; its result belongs to the number it was sent to.
      if ( phone !== this.activePhone ) return;
      this.demoSendOk = result.ok;
      this.demoSendMessage = result.ok || !outcome
        ? this.t( result.key )
        : `${this.t( result.key )} (${outcome})`;
      await this.loadConversation( phone );
    },
    formatDate( iso ) {
      const parsed = new Date( iso );
      return isNaN( parsed.getTime() ) ? '' : parsed.toLocaleDateString();
    },
    formatDateTime( iso ) {
      const parsed = new Date( iso );
      return isNaN( parsed.getTime() ) ? '' : parsed.toLocaleString();
    },
  },
};
</script>

<style scoped>
/* px, never rem: this app sets html { font-size: 10px }. */
.wa-page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.wa-refresh-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.wa-refresh-icon {
  font-size: 18px;
  line-height: 1;
}

.wa-refresh-icon.is-spinning {
  animation: wa-refresh-spin 0.9s linear infinite;
}

@keyframes wa-refresh-spin {
  to { transform: rotate(360deg); }
}

.wa-conversations {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

/* This app sets html { font-size: 10px }, so every rem here -- Bootstrap's
   included -- is 10px, not the usual 16px. Sizes below are therefore given in
   px: it keeps them honest and stops the next person writing 1.15rem and
   quietly getting 11.5px, which is exactly what happened the first time. */
.wa-conversations-body {
  padding-top: 110px;
  padding-bottom: 30px;
  flex: 1 0 auto;
  font-size: 15px;
}

.wa-conversations-body h4 {
  font-size: 24px;
}

.wa-conversations-body > p {
  font-size: 15px;
}

.wa-conversations-body >>> .form-control,
.wa-conversations-body >>> .btn {
  font-size: 15px;
}

.wa-contact-list {
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 60vh;
  overflow-y: auto;
  border: 1px solid #e6e6e6;
  border-radius: 6px;
}

.wa-contact {
  display: flex;
  flex-wrap: wrap;
  gap: 2px 8px;
  padding: 12px 14px;
  font-size: 15px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
}

.wa-contact:last-child {
  border-bottom: none;
}

.wa-contact:hover,
.wa-contact.is-active {
  background-color: #f2fbf7;
}

.wa-contact-name {
  font-weight: 600;
  width: 100%;
}

.wa-contact-phone {
  font-family: monospace;
  font-size: 14px;
  color: #1e4d3f;
}

.wa-contact-date {
  font-size: 13px;
  color: #6c757d;
}

.wa-thread-header {
  font-size: 19px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: baseline;
  padding-bottom: 8px;
  border-bottom: 1px solid #e6e6e6;
}

.wa-demo-send {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
  font-size: 14px;
}

.wa-thread {
  margin-top: 12px;
  max-height: 62vh;
  overflow-y: auto;
  padding-inline-end: 4px;
}

.wa-message {
  max-width: 78%;
  padding: 11px 15px;
  border-radius: 10px;
  margin-bottom: 12px;
  font-size: 17px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Inbound starts the row, outbound ends it. Logical properties, so the sides
   swap automatically with the RTL/LTR direction on the root element. */
.wa-in {
  background-color: #f1f3f5;
  margin-inline-end: auto;
}

.wa-out {
  background-color: #d9f5e6;
  margin-inline-start: auto;
}

.wa-message.is-failed {
  background-color: #fdecee;
}

/* Throttled: we decided not to send it. Muted rather than red -- nothing went
   wrong, we declined on purpose -- but struck through so it can never be read
   as a message the traveller actually received. */
.wa-message.is-rate-limited {
  background-color: #f3f4f6;
  opacity: 0.7;
}

.wa-message.is-rate-limited .wa-message-body {
  text-decoration: line-through;
}

.wa-message-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
  font-size: 13px;
  color: #6c757d;
}

.wa-tag {
  font-size: 13px;
  background-color: rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  padding: 1px 8px;
}

.wa-message-error {
  margin-top: 5px;
  font-size: 13.5px;
  color: #b02a37;
}

.wa-badge {
  font-size: 13px;
  padding: 1px 8px;
  border-radius: 10px;
  background-color: #e9ecef;
  color: #495057;
}
</style>
